import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../api/http';
import {
  Notice,
  SchoolSelect,
  errorMessage,
  primaryButton,
  selectClass,
} from '../../components/common';
import { t, type MessageKey } from '../../i18n';
import type { Exam } from './ExamsTab';

type MarkStatus = 'present' | 'absent' | 'exempt';
interface Sheet {
  exam: { id: string; name: string; status: string };
  paper: {
    id: string;
    subjectName: string;
    className: string;
    maxMarks: number;
    passMarks: number;
  };
  canEnter: boolean;
  entered: number;
  students: {
    studentId: string;
    name: string;
    rollNumber: string | null;
    sectionName: string | null;
    status: MarkStatus | null;
    marks: number | null;
    remarks: string | null;
  }[];
}
interface PaperRef {
  id: string;
  className: string;
  subjectName: string;
}

type Draft = Record<string, { status: MarkStatus; marks: string }>;

/** Marks entry for teachers (their own sections) and administrators (everything). */
export function MarksPage() {
  const [schoolId, setSchoolId] = useState('');
  const [examId, setExamId] = useState('');
  const [paperId, setPaperId] = useState('');

  const exams = useQuery({
    queryKey: ['exams', schoolId],
    queryFn: () => api<Exam[]>(`/schools/${schoolId}/exams`),
    enabled: !!schoolId,
  });
  const detail = useQuery({
    queryKey: ['exams', schoolId, examId],
    queryFn: () => api<{ papers: PaperRef[] }>(`/schools/${schoolId}/exams/${examId}`),
    enabled: !!schoolId && !!examId,
  });
  const open = exams.data?.filter((x) => x.status !== 'draft') ?? [];

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <h1 className="text-2xl font-bold">{t('marks.title')}</h1>
      <div className="flex flex-wrap items-end gap-4">
        <SchoolSelect
          value={schoolId}
          onChange={(id) => {
            setSchoolId(id);
            setExamId('');
            setPaperId('');
          }}
        />
        <div>
          <label htmlFor="marks-exam" className="block text-sm font-medium">
            {t('marks.exam')}
          </label>
          <select
            id="marks-exam"
            value={examId}
            onChange={(e) => {
              setExamId(e.target.value);
              setPaperId('');
            }}
            className={selectClass}
          >
            <option value="">{t('marks.choose')}</option>
            {open.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name} ({t(`exam.status.${x.status}` as MessageKey)})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="marks-paper" className="block text-sm font-medium">
            {t('marks.paper')}
          </label>
          <select
            id="marks-paper"
            value={paperId}
            onChange={(e) => setPaperId(e.target.value)}
            className={selectClass}
          >
            <option value="">{t('marks.choose')}</option>
            {detail.data?.papers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.className} – {p.subjectName}
              </option>
            ))}
          </select>
        </div>
      </div>
      {exams.data && open.length === 0 && <p className="text-slate-600">{t('marks.noExams')}</p>}
      {schoolId && examId && paperId && (
        <SheetPanel
          key={`${examId}-${paperId}`}
          schoolId={schoolId}
          examId={examId}
          paperId={paperId}
        />
      )}
    </div>
  );
}

function SheetPanel({
  schoolId,
  examId,
  paperId,
}: {
  schoolId: string;
  examId: string;
  paperId: string;
}) {
  const queryClient = useQueryClient();
  const url = `/schools/${schoolId}/exams/${examId}/papers/${paperId}/marks`;
  const [draft, setDraft] = useState<Draft>({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const sheet = useQuery({ queryKey: ['marks', url], queryFn: () => api<Sheet>(url) });
  const data = sheet.data;

  useEffect(() => {
    if (!data) return;
    setDraft(
      Object.fromEntries(
        data.students.map((s) => [
          s.studentId,
          { status: s.status ?? 'present', marks: s.marks === null ? '' : String(s.marks) },
        ]),
      ),
    );
  }, [data]);

  const save = useMutation({
    mutationFn: (body: object) => api<Sheet>(url, { method: 'PUT', body }),
    onSuccess: (saved) => {
      setMessage({ kind: 'ok', text: t('marks.saved') });
      queryClient.setQueryData(['marks', url], saved);
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });

  if (sheet.isPending) return <p role="status">{t('common.loading')}</p>;
  if (sheet.isError || !data) return <Notice kind="error">{errorMessage(sheet.error)}</Notice>;

  const editable = data.canEnter;
  const submit = () => {
    setMessage(null);
    const records = data.students
      .filter(
        (s) =>
          draft[s.studentId] &&
          (draft[s.studentId]!.status !== 'present' || draft[s.studentId]!.marks !== ''),
      )
      .map((s) => {
        const d = draft[s.studentId]!;
        return d.status === 'present'
          ? { studentId: s.studentId, status: d.status, marks: Number(d.marks) }
          : { studentId: s.studentId, status: d.status };
      });
    if (records.length === 0) return;
    save.mutate({ records });
  };

  return (
    <section
      aria-label={`${data.paper.className} – ${data.paper.subjectName}`}
      className="space-y-3 rounded-lg border border-slate-200 bg-white p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">
          {data.paper.className} – {data.paper.subjectName}
        </h2>
        <p className="text-sm text-slate-600">
          {t('marks.outOf', { max: data.paper.maxMarks, pass: data.paper.passMarks })} ·{' '}
          {t('marks.entered', { entered: data.entered, total: data.students.length })}
        </p>
      </div>
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {!editable && <Notice kind="error">{t('marks.closed')}</Notice>}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase text-slate-600">
            <tr>
              <th scope="col" className="p-2">
                {t('marks.student')}
              </th>
              <th scope="col" className="p-2">
                {t('marks.section')}
              </th>
              <th scope="col" className="p-2">
                {t('marks.status')}
              </th>
              <th scope="col" className="p-2">
                {t('marks.marks')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.students.map((s) => {
              const d = draft[s.studentId] ?? { status: 'present' as MarkStatus, marks: '' };
              return (
                <tr key={s.studentId}>
                  <th scope="row" className="p-2 font-medium">
                    {s.name}
                  </th>
                  <td className="p-2">{s.sectionName ?? '—'}</td>
                  <td className="p-2">
                    <select
                      aria-label={t('marks.statusFor', { name: s.name })}
                      disabled={!editable}
                      value={d.status}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          [s.studentId]: {
                            status: e.target.value as MarkStatus,
                            marks: e.target.value === 'present' ? d.marks : '',
                          },
                        })
                      }
                      className={selectClass}
                    >
                      {(['present', 'absent', 'exempt'] as const).map((x) => (
                        <option key={x} value={x}>
                          {t(`marks.${x}` as MessageKey)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="p-2">
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      max={data.paper.maxMarks}
                      aria-label={t('marks.marksFor', { name: s.name })}
                      disabled={!editable || d.status !== 'present'}
                      value={d.marks}
                      onChange={(e) =>
                        setDraft({ ...draft, [s.studentId]: { ...d, marks: e.target.value } })
                      }
                      className="w-28 rounded border border-slate-300 px-2 py-1.5 text-sm"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editable && (
        <button type="button" className={primaryButton} disabled={save.isPending} onClick={submit}>
          {t('marks.save')}
        </button>
      )}
    </section>
  );
}
